import { App as CapacitorApp } from "@capacitor/app";
import { Clipboard } from "@capacitor/clipboard";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Keyboard } from "@capacitor/keyboard";
import { LocalNotifications } from "@capacitor/local-notifications";
import { Preferences } from "@capacitor/preferences";
import { Share } from "@capacitor/share";
import { StatusBar, Style } from "@capacitor/status-bar";

export type RuntimePlatform = "web" | "ios" | "android";

export type RuntimeListener = {
  remove: () => Promise<void>;
};

export type RuntimeNotificationPermission =
  | "prompt"
  | "prompt-with-rationale"
  | "granted"
  | "denied";

export type RuntimeNotification = {
  id: number;
  title: string;
  body: string;
  scheduleAt: Date;
  extra?: Record<string, unknown>;
  sound?: string;
  smallIcon?: string;
  largeIcon?: string;
};

export type RuntimeClientDependencies = {
  app?: typeof CapacitorApp;
  capacitor?: Pick<typeof Capacitor, "getPlatform" | "isNativePlatform">;
  clipboard?: typeof Clipboard;
  device?: typeof Device;
  filesystem?: typeof Filesystem;
  keyboard?: typeof Keyboard;
  notifications?: typeof LocalNotifications;
  preferences?: typeof Preferences;
  share?: typeof Share;
  statusBar?: typeof StatusBar;
  desktopClipboardWrite?: (text: string) => Promise<boolean>;
};

const _runtimePlatform = (value: string): RuntimePlatform => {
  if (value === "web" || value === "ios" || value === "android") return value;
  throw new Error(`Unsupported runtime platform: ${value}`);
};

export const getRuntimePlatform = (): RuntimePlatform =>
  _runtimePlatform(Capacitor.getPlatform());

export const isNativeRuntime = (): boolean => Capacitor.isNativePlatform();

export const createRuntimeClient = (
  dependencies: RuntimeClientDependencies = {}
) => {
  const app = dependencies.app || CapacitorApp;
  const capacitor = dependencies.capacitor || Capacitor;
  const clipboard = dependencies.clipboard || Clipboard;
  const device = dependencies.device || Device;
  const filesystem = dependencies.filesystem || Filesystem;
  const keyboard = dependencies.keyboard || Keyboard;
  const notifications = dependencies.notifications || LocalNotifications;
  const preferences = dependencies.preferences || Preferences;
  const share = dependencies.share || Share;
  const statusBar = dependencies.statusBar || StatusBar;
  const platform = _runtimePlatform(capacitor.getPlatform());

  return {
    platform,
    isNative: capacitor.isNativePlatform(),

    storage: {
      getItem: async (key: string): Promise<string | null> => {
        const { value } = await preferences.get({ key });
        return value;
      },
      setItem: async (key: string, value: string): Promise<void> => {
        await preferences.set({ key, value });
      },
      removeItem: async (key: string): Promise<void> => {
        await preferences.remove({ key });
      },
      keys: async (): Promise<string[]> => (await preferences.keys()).keys,
    },

    clipboard: {
      write: async (text: string): Promise<void> => {
        if (await dependencies.desktopClipboardWrite?.(text)) return;
        await clipboard.write({ string: text });
      },
    },

    device: {
      getInfo: () => device.getInfo(),
      getId: async (): Promise<string> => (await device.getId()).identifier,
      getLanguageTag: async (): Promise<string> =>
        (await device.getLanguageTag()).value,
    },

    files: {
      writeCacheFile: async (
        path: string,
        data: string
      ): Promise<string> =>
        (
          await filesystem.writeFile({
            path,
            data,
            directory: Directory.Cache,
          })
        ).uri,
    },

    keyboard: {
      onWillShow: (listener: () => void): Promise<RuntimeListener> =>
        /* action-id: lifecycle.keyboard.visibility */ keyboard.addListener(
          "keyboardWillShow",
          listener
        ),
      onDidShow: (listener: () => void): Promise<RuntimeListener> =>
        /* action-id: lifecycle.keyboard.visibility */ keyboard.addListener(
          "keyboardDidShow",
          listener
        ),
      onDidHide: (listener: () => void): Promise<RuntimeListener> =>
        /* action-id: lifecycle.keyboard.visibility */ keyboard.addListener(
          "keyboardDidHide",
          listener
        ),
    },

    share: async (input: {
      title?: string;
      text?: string;
      url?: string;
      dialogTitle?: string;
    }): Promise<void> => {
      await share.share(input);
    },

    app: {
      onStateChange: (
        listener: (state: { isActive: boolean }) => void
      ): Promise<RuntimeListener> => {
        return /* action-id: lifecycle.app.foreground */ app.addListener(
          "appStateChange",
          listener
        );
      },
      onUrlOpen: (
        listener: (event: { url: string }) => void
      ): Promise<RuntimeListener> => {
        return /* action-id: lifecycle.url.open */ app.addListener(
          "appUrlOpen",
          listener
        );
      },
      onResume: (listener: () => void): Promise<RuntimeListener> => {
        return /* action-id: lifecycle.app.foreground */ app.addListener(
          "resume",
          listener
        );
      },
      onBackButton: (
        listener: (event: { canGoBack: boolean }) => void
      ): Promise<RuntimeListener> => {
        return /* action-id: lifecycle.app.back */ app.addListener(
          "backButton",
          listener
        );
      },
      getLaunchUrl: async (): Promise<string | undefined> =>
        (await app.getLaunchUrl())?.url,
      minimize: async (): Promise<void> => {
        await app.minimizeApp();
      },
    },

    notifications: {
      checkPermission: async (): Promise<RuntimeNotificationPermission> =>
        (await notifications.checkPermissions()).display,
      requestPermission: async (): Promise<RuntimeNotificationPermission> =>
        (await notifications.requestPermissions()).display,
      onAction: (
        listener: (event: {
          notification: { id: number; extra?: Record<string, unknown> };
        }) => void
      ): Promise<RuntimeListener> => {
        return /* action-id: lifecycle.notification.open */ notifications.addListener(
          "localNotificationActionPerformed",
          listener
        );
      },
      schedule: async (items: RuntimeNotification[]): Promise<void> => {
        await notifications.schedule({
          notifications: items.map((item) => ({
            id: item.id,
            title: item.title,
            body: item.body,
            schedule: { at: item.scheduleAt },
            extra: item.extra,
            sound: item.sound,
            smallIcon: item.smallIcon,
            largeIcon: item.largeIcon,
          })),
        });
      },
      cancel: async (ids: number[]): Promise<void> => {
        await notifications.cancel({
          notifications: ids.map((id) => ({ id })),
        });
      },
    },

    statusBar: {
      setStyle: async (style: "dark" | "light"): Promise<void> => {
        await statusBar.setStyle({
          style: style === "dark" ? Style.Dark : Style.Light,
        });
      },
      setBackgroundColor: async (color: string): Promise<void> => {
        await statusBar.setBackgroundColor({ color });
      },
    },
  };
};

export type RuntimeClient = ReturnType<typeof createRuntimeClient>;

export const getRuntimeDeviceLanguageTag = async (): Promise<string> =>
  (await Device.getLanguageTag()).value;
